const MS_TO_KT = 1.94384;
const CALM_KT_THRESHOLD = 5;
const PENNANT_KT = 50;
const FULL_KT = 10;
const HALF_KT = 5;

// Skysight-style horizontal-stem barb.
// Origin (0,0) is the anchor (placed at grid x,y). The shaft runs horizontally
// from x=-10 to x=10 at y=-2.5. The TAIL TICK (open end into the wind source) is
// at the left end. Features extend UPWARD from the shaft.
//
// Rotation convention: dirDeg is the meteorological "FROM" direction (clockwise
// from north). At rotate(0), the tail points LEFT (west). To rotate it to compass
// north we add 90°. So the final transform applies rotate(dirDeg + 90).
const SHAFT_LEFT = -10;
const SHAFT_RIGHT = 10;
const SHAFT_Y = -2.5;
const TAIL_TICK = 5;
const FEATURE_HEIGHT = 5;
const PENNANT_HALF_BASE = 2.5;      // 5-unit-wide pennant (matches Skysight's path)
const PENNANT_GAP = 1;              // gap after a pennant before the next feature
const FEATURE_SPACING = 3;          // gap between full/half barbs (Skysight uses 3)
const FEATURE_START_OFFSET = 3;     // first feather sits 3 units inside the tail tick
const SCALE = 0.9;
const ROTATION_OFFSET = 90;

const buildBarbPath = (knots) => {
    const parts = [];
    parts.push(`M${SHAFT_LEFT},${SHAFT_Y}L${SHAFT_RIGHT},${SHAFT_Y}`);
    parts.push(`M${SHAFT_LEFT},${SHAFT_Y}L${SHAFT_LEFT},${SHAFT_Y + TAIL_TICK}`);

    let remaining = Math.round(knots / 5) * 5;
    const pennants = Math.floor(remaining / PENNANT_KT); remaining -= pennants * PENNANT_KT;
    const fulls = Math.floor(remaining / FULL_KT); remaining -= fulls * FULL_KT;
    const halves = Math.floor(remaining / HALF_KT);

    // Features extend DOWNWARD from the shaft. Pennants are filled triangles
    // anchored at the tail end (50 kt each). Full and half barbs are perpendicular
    // vertical lines, matching Skysight's bundle path pattern. After rotation by
    // (dirDeg + 90), all features end up on the LEFT side of the shaft — the
    // Northern Hemisphere meteorological convention.
    let cursor = pennants > 0 ? SHAFT_LEFT : SHAFT_LEFT + FEATURE_START_OFFSET;
    for (let i = 0; i < pennants; i++) {
        const base = cursor;
        const tip = cursor + 2 * PENNANT_HALF_BASE;
        parts.push(`M${base},${SHAFT_Y}L${base + PENNANT_HALF_BASE},${SHAFT_Y + FEATURE_HEIGHT}L${tip},${SHAFT_Y}Z`);
        cursor = tip + PENNANT_GAP;
    }
    for (let i = 0; i < fulls; i++) {
        parts.push(`M${cursor},${SHAFT_Y}L${cursor},${SHAFT_Y + FEATURE_HEIGHT}`);
        cursor += FEATURE_SPACING;
    }
    for (let i = 0; i < halves; i++) {
        parts.push(`M${cursor},${SHAFT_Y}L${cursor},${SHAFT_Y + FEATURE_HEIGHT / 2}`);
        cursor += FEATURE_SPACING;
    }

    return parts.join('');
};

const WindBarb = ({ speedMs, dirDeg, color = 'currentColor', x = 0, y = 0 }) => {
    const knots = (speedMs || 0) * MS_TO_KT;

    if (knots < CALM_KT_THRESHOLD) {
        return (
            <g transform={`translate(${x},${y})`} data-testid="barb-root">
                <circle cx={0} cy={0} r={1.8} fill="none" stroke={color} strokeWidth={1} data-testid="barb-calm" />
            </g>
        );
    }

    // Per-feature counts (used as keys for hidden test markers)
    let remaining = Math.round(knots / 5) * 5;
    const pennants = Math.floor(remaining / PENNANT_KT); remaining -= pennants * PENNANT_KT;
    const fulls = Math.floor(remaining / FULL_KT); remaining -= fulls * FULL_KT;
    const halves = Math.floor(remaining / HALF_KT);

    return (
        <g
            transform={`translate(${x},${y}) rotate(${(dirDeg || 0) + ROTATION_OFFSET}) scale(${SCALE})`}
            data-testid="barb-root"
        >
            <path
                d={buildBarbPath(knots)}
                stroke={color}
                fill={color}
                strokeWidth={1.6}
                strokeLinecap="round"
                strokeLinejoin="round"
            />
            {/* Invisible markers so the existing tests can count rendered feature types. */}
            {Array.from({ length: pennants }).map((_, i) => (
                <metadata key={`p${i}`} data-testid="barb-pennant" />
            ))}
            {Array.from({ length: fulls }).map((_, i) => (
                <metadata key={`f${i}`} data-testid="barb-full" />
            ))}
            {Array.from({ length: halves }).map((_, i) => (
                <metadata key={`h${i}`} data-testid="barb-half" />
            ))}
        </g>
    );
};

export default WindBarb;
